import { FC } from 'react'

import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { ColumnSort, flexRender, Header } from '@tanstack/react-table'
import { FaPowerOff } from 'react-icons/fa'
import { FiAlertTriangle } from 'react-icons/fi'
import { MdOutlineWifi } from 'react-icons/md'
import { TiArrowSortedDown, TiArrowSortedUp, TiArrowUnsorted } from 'react-icons/ti'

import { getColOffset } from './assetTableCellHelper'
import { getStickyBorderClass, isDraggable } from './utils'

import { AssetStateSummary } from '../types'

interface SortableHeaderProps {
  header: Header<AssetStateSummary, unknown>
  sorting: ColumnSort[]
  setSorting: (sorting: ColumnSort[]) => void
  lastColumnId: string
  fillSpace: number
}

const ASSET_SUB_SORT_META = {
  alertSeverity: { Icon: FiAlertTriangle, label: 'Alert severity' },
  powerOn: { Icon: FaPowerOff, label: 'Power' },
  gatewayOn: { Icon: MdOutlineWifi, label: 'Gateway' },
} as const

const ASSET_SUB_SORT_IDS = Object.keys(ASSET_SUB_SORT_META) as (keyof typeof ASSET_SUB_SORT_META)[]

const SortableHeader: FC<SortableHeaderProps> = ({ header, sorting, setSorting, lastColumnId, fillSpace }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: header.column.id })
  const isAssetCol = header.column.id === 'asset'
  const isLastCol = header.column.id === lastColumnId

  const size = header.getSize() + (header.column.id === lastColumnId ? fillSpace : 0)
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    width: `${size}px`,
    left: getColOffset(header.column.id),
    opacity: isDragging ? 0.4 : 1,
  }

  const toggleSort = (fieldId: string) => {
    const current = sorting.find((s) => s.id === fieldId)
    if (!current) {
      setSorting([{ id: fieldId, desc: true }])
    } else if (current.desc) {
      setSorting([{ id: fieldId, desc: false }])
    } else {
      setSorting([])
    }
  }

  const getSortIcon = (fieldId: string, className = 'text-lg') => {
    const current = sorting.find((s) => s.id === fieldId)
    // Unsorted arrows are hidden until the header row is hovered; the active sort stays visible.
    if (!current)
      return <TiArrowUnsorted className={`${className} opacity-0 transition-opacity group-hover:opacity-100`} />
    if (current.desc) return <TiArrowSortedDown className={className} />
    return <TiArrowSortedUp className={className} />
  }

  return (
    <th
      ref={setNodeRef}
      style={style}
      className={`${!isDraggable(header.column.id) ? 'z-10 bg-primary text-primary-content ' + getStickyBorderClass(header.column.id) : 'z-10 border-r'}
       border-gray-200 wrap-break-words whitespace-normal select-none cursor-pointer p-0`}
      onClick={isAssetCol ? undefined : header.column.getToggleSortingHandler()}
    >
      <div
        className={`flex w-full h-full items-center justify-between px-0 relative ${
          isLastCol && !isAssetCol ? 'pr-3' : ''
        }`}
      >
        {isAssetCol ? (
          <div className={`flex items-center w-full h-full ${isLastCol ? 'pr-3' : ''}`}>
            <div
              className="flex-1 flex items-center gap-0.5 text-xs pl-2 py-2 cursor-pointer"
              onClick={() => toggleSort('name')}
            >
              {flexRender(header.column.columnDef.header, header.getContext())}
              <span className="ml-auto">{getSortIcon('name')}</span>
            </div>
            <div className="shrink-0 self-stretch pr-[5px] flex">
              {ASSET_SUB_SORT_IDS.map((id) => {
                const { Icon, label } = ASSET_SUB_SORT_META[id]
                return (
                  <button
                    key={id}
                    onClick={(e) => {
                      e.stopPropagation()
                      toggleSort(id)
                    }}
                    data-tip={label}
                    aria-label={label}
                    className="tooltip tooltip-bottom flex items-center justify-between gap-0.5 w-[55px] h-full pl-[5px] pr-2 border-l border-primary-content/40 cursor-pointer hover:bg-primary-focus/20"
                  >
                    <Icon className="text-primary-content w-4 h-4" />
                    {getSortIcon(id)}
                  </button>
                )
              })}
            </div>
          </div>
        ) : (
          <>
            <div className="flex-1 flex items-center gap-0.5 w-full h-full text-xs pl-2 py-2">
              <span className="truncate">{flexRender(header.column.columnDef.header, header.getContext())}</span>
              <span className="ml-auto">
                {{
                  asc: <TiArrowSortedUp className="text-lg" />,
                  desc: <TiArrowSortedDown className="text-lg" />,
                }[header.column.getIsSorted() as 'asc' | 'desc'] ?? (
                  <TiArrowUnsorted className="text-lg opacity-0 transition-opacity group-hover:opacity-100" />
                )}
              </span>
            </div>
            {isDraggable(header.column.id) && (
              <span {...listeners} {...attributes} className="flex cursor-grab w-5 items-center justify-center h-full">
                ⋮⋮
              </span>
            )}
          </>
        )}
        {header.column.getCanResize() && (
          <div
            onMouseDown={(e) => {
              e.stopPropagation()
              header.getResizeHandler()(e)
            }}
            onTouchStart={(e) => {
              e.stopPropagation()
              header.getResizeHandler()(e)
            }}
            onClick={(e) => e.stopPropagation()}
            className={`absolute right-0 top-0 w-1 h-full cursor-col-resize select-none touch-none ${
              header.column.getIsResizing()
                ? 'bg-primary-content opacity-100'
                : 'opacity-0 hover:opacity-100 bg-primary-content/50'
            }`}
          />
        )}
      </div>
    </th>
  )
}

export default SortableHeader
